import { memo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Box, InputBase, Button, Slide } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { useSearchContext } from '../../contexts/SearchContext';
import useLocalization from '../../hooks/useLocalization';
import useSearchAvailability from '../../hooks/useSearchAvailability';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import { useMarketplaceConfig } from '../../queries/marketplace';

import { SkinnedSearchEndAdornment } from '../../config/skinRegistry/components';
import { SKIN_STYLES } from '../../styles/components/searchBar';
import { mergeSx } from '../../utils/styles';

const SearchBar = memo(() => {
  const { searchValue, handleSearch, handleClear, showSearchBar, setShowSearchBar, isFetching } = useSearchContext();

  const { t } = useLocalization();
  const { isSearchAvailable } = useSearchAvailability();
  const styles = useSkinStyles(SKIN_STYLES);
  const location = useLocation();
  const navigate = useNavigate();

  const { data } = useMarketplaceConfig();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const feedRunning = data?.feedRunning ?? false;
  const isOnSearchPage = location.pathname === '/search';
  const shouldShowSearchBar = showSearchBar || isOnSearchPage;

  const handleClose = useCallback(() => {
    if (isOnSearchPage) {
      navigate(-1);
      handleClear();
    } else {
      setShowSearchBar(false);
      handleClear();
    }
  }, [handleClear, isOnSearchPage, navigate, setShowSearchBar]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSearch(event.target.value);
    },
    [handleSearch]
  );

  const handleFocus = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  if (!isSearchAvailable) return null;

  return (
    <Slide
      direction='left'
      in={shouldShowSearchBar}
      timeout={300}
      mountOnEnter
      unmountOnExit={false}
      appear={false}
      onEntered={handleFocus}
    >
      <Box
        sx={mergeSx(styles.searchWrapper, {
          opacity: shouldShowSearchBar ? 1 : 0.25,
          transition: 'opacity 300ms',
        })}
      >
        <Box sx={styles.search}>
          <Box sx={styles.searchIconWrapper}>
            <SearchIcon />
          </Box>
          <InputBase
            inputRef={inputRef}
            sx={styles.inputBase}
            placeholder={t('search')}
            inputProps={{ 'aria-label': 'search' }}
            value={searchValue}
            onChange={handleInputChange}
            disabled={!feedRunning}
            endAdornment={
              <SkinnedSearchEndAdornment isFetching={isFetching} searchValue={searchValue} handleClear={handleClear} />
            }
          />
        </Box>
        <Button sx={styles.closeButton} onClick={handleClose}>
          {t('close')}
        </Button>
      </Box>
    </Slide>
  );
});

export default SearchBar;
